import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useRefresh } from './RefreshContext';
import CreateVideoSection from './CreateVideoSection';
import CreateArticleSection from './CreateArticleSection';
import CreateEventSection from './CreateEventSection';
import ApproveVideosSection from './ApproveVideosSection';
import ApproveArticlesSection from './ApproveArticlesSection';
import ApproveEventsSection from './ApproveEventsSection';
import AdminYoutubeTab from './AdminYoutubeTab';
import AdminArticlesTab from './AdminArticlesTab';
import AdminEventsTab from './AdminEventsTab';
import MyVideosSection from './MyVideosSection';
import MyArticlesSection from './MyArticlesSection';
import MyEventsSection from './MyEventsSection';
import { T } from './theme';

const API_BASE = 'https://d3bdqxkpn753kf.cloudfront.net';
const SECTION_ICONS = { videos: '▶️', articles: '📖', events: '📅' };
const SUBTAB_ICONS  = { create: '✏️', approve: '✅', my: '👤', manage: '⚙️' };

const ContentManagementPage = () => {
  const { role } = useAuth();
  const { refresh } = useRefresh();
  const [activeSection, setActiveSection] = useState('videos');
  const [activeSubTab, setActiveSubTab] = useState('create');
  const [pendingVideos, setPendingVideos] = useState(0);
  const [pendingArticles, setPendingArticles] = useState(0);
  const [myVideos, setMyVideos] = useState(0);
  const [myArticles, setMyArticles] = useState(0);

  const userEmail = localStorage.getItem('vrvEmail') || '';
  const roles = (role || '').split(',').map(s => s.trim().toUpperCase());
  const isAdmin         = roles.includes('ADMIN');
  const isCreator       = roles.includes('CONTENT-CREATOR');
  const isApprover      = roles.includes('CONTENT-APPROVER');
  const canCreate       = isAdmin || isCreator;
  const canApprove      = isAdmin || isApprover;
  const canSeeMyContent = isAdmin || isCreator;

  useEffect(() => {
    fetch(`${API_BASE}/vrv-youtubecontent-getAll`)
      .then(r => r.json()).then(data => {
        const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        const all = parsed.data || [];
        // Creators see their own pending; approvers/admins see all pending
        const count = canApprove
          ? all.filter(v => v.status === 'PENDING').length
          : all.filter(v => v.status === 'PENDING' && (v.createdBy || '').toLowerCase() === userEmail.toLowerCase()).length;
        setPendingVideos(count);
        setMyVideos(all.filter(v => (v.createdBy || '').toLowerCase() === userEmail.toLowerCase()).length);
      }).catch(() => {});
    fetch(`${API_BASE}/vrv-articlecontent-getAll`)
      .then(r => r.json()).then(data => {
        const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        const all = parsed.data || [];
        const count = canApprove
          ? all.filter(a => a.status === 'PENDING').length
          : all.filter(a => a.status === 'PENDING' && (a.createdBy || '').toLowerCase() === userEmail.toLowerCase()).length;
        setPendingArticles(count);
        setMyArticles(all.filter(a => (a.createdBy || '').toLowerCase() === userEmail.toLowerCase()).length);
      }).catch(() => {});
  }, [refresh]); // eslint-disable-line

  const sections = [
    { id: 'videos',   label: 'Videos' },
    { id: 'articles', label: 'Articles' },
    ...(isAdmin ? [{ id: 'events', label: 'Events' }] : []),
  ];

  const buildSubTabs = (sec) => {
    if (sec === 'events') {
      return [
        { id: 'create', label: 'Create Event' },
        { id: 'my',     label: 'My Events' },
        { id: 'manage', label: 'Manage All' },
      ];
    }
    const tabs = [];
    if (canCreate)       tabs.push({ id: 'create',  label: `Create ${sec === 'videos' ? 'Video' : 'Article'}` });
    if (canApprove)      tabs.push({ id: 'approve', label: 'Approve' });
    if (canSeeMyContent) tabs.push({ id: 'my',      label: `My ${sec === 'videos' ? 'Videos' : 'Articles'}` });
    if (isAdmin)         tabs.push({ id: 'manage',  label: 'Manage All' });
    return tabs;
  };

  const subTabs = buildSubTabs(activeSection);
  const effectiveSubTab = subTabs.find(s => s.id === activeSubTab) ? activeSubTab : subTabs[0]?.id;

  const handleSectionChange = (id) => {
    setActiveSection(id);
    const tabs = buildSubTabs(id);
    setActiveSubTab(tabs[0]?.id || 'create');
  };

  // Smart navigation: approvers/admins go to Approve tab, creators go to My tab
  const handlePendingClick = (section) => {
    setActiveSection(section);
    setActiveSubTab(canApprove ? 'approve' : 'my');
    setTimeout(() => window.scrollTo({ top: 300, behavior: 'smooth' }), 50);
  };

  // Build KPI cards based on role
  const kpiCards = [
    {
      icon: <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.723v6.554a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" /></svg>,
      value: pendingVideos,
      label: canApprove ? 'Videos to Approve' : 'My Pending Videos',
      clickable: canCreate || canApprove,
      onClick: () => handlePendingClick('videos'),
      highlight: pendingVideos > 0,
      warn: true,
    },
    {
      icon: <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
      value: pendingArticles,
      label: canApprove ? 'Articles to Approve' : 'My Pending Articles',
      clickable: canCreate || canApprove,
      onClick: () => handlePendingClick('articles'),
      highlight: pendingArticles > 0,
      warn: true,
    },
    ...(canCreate ? [
      {
        icon: <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.723v6.554a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" /></svg>,
        value: myVideos,
        label: 'My Videos',
        clickable: true,
        onClick: () => { setActiveSection('videos'); setActiveSubTab('my'); setTimeout(() => window.scrollTo({ top: 300, behavior: 'smooth' }), 50); },
        highlight: myVideos > 0,
      },
      {
        icon: <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
        value: myArticles,
        label: 'My Articles',
        clickable: true,
        onClick: () => { setActiveSection('articles'); setActiveSubTab('my'); setTimeout(() => window.scrollTo({ top: 300, behavior: 'smooth' }), 50); },
        highlight: myArticles > 0,
      },
    ] : []),
    {
      icon: <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
      value: canCreate ? '✓' : '✕',
      label: 'Can Create',
      clickable: false,
      highlight: canCreate,
    },
    {
      icon: <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
      value: canApprove ? '✓' : '✕',
      label: 'Can Approve',
      clickable: false,
      highlight: canApprove,
    },
    {
      icon: <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
      value: isAdmin ? '✓' : '✕',
      label: 'Admin Access',
      clickable: false,
      highlight: isAdmin,
    },
  ];

  return (
    <div style={{ background: T.bg, color: T.ink, minHeight: '100vh' }}>

      {/* Hero with KPI Dashboard */}
      <section className="vrv-hero">
        <div className="vrv-container">
          <div className="relative z-10">
            <p className="vrv-tag mb-3">Creator Tools</p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>Content Management</h1>
            <p className="text-base mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>Create, approve and manage videos and articles.</p>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 max-w-4xl">
              {kpiCards.map((k, i) =>
                k.clickable ? (
                  <button key={i} onClick={k.onClick}
                    className="vrv-kpi-card text-left transition-all duration-200 hover:scale-[1.04]"
                    style={{ cursor: 'pointer', border: k.highlight ? '1px solid rgba(212,175,55,0.5)' : '1px solid rgba(212,175,55,0.2)' }}>
                    <div className="mb-1.5" style={{ color: 'rgba(255,255,255,0.8)' }}>{k.icon}</div>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {k.value}
                      {k.warn && k.value > 0 && <span className="text-sm ml-1" style={{ color: T.gold }}>⚠</span>}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider" style={{ color: k.highlight ? T.gold : 'rgba(255,255,255,0.5)' }}>{k.label}</p>
                  </button>
                ) : (
                  <div key={i} className="vrv-kpi-card">
                    <div className="mb-1.5" style={{ color: 'rgba(255,255,255,0.8)' }}>{k.icon}</div>
                    <p className="text-2xl font-bold" style={{ fontFamily: 'Poppins, sans-serif', color: k.highlight ? T.gold : 'rgba(255,255,255,0.4)' }}>{k.value}</p>
                    <p className="text-[10px] uppercase tracking-wider" style={{ color: k.highlight ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.4)' }}>{k.label}</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="vrv-container py-8" style={{ overflowAnchor: 'none' }}>

        {/* Section Selector Cards */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {sections.map(sec => {
            const active = activeSection === sec.id;
            return (
              <button key={sec.id} onClick={() => handleSectionChange(sec.id)}
                className="flex flex-col items-center gap-1 py-2.5 px-2 rounded-2xl"
                style={{
                  background: active ? T.navy : 'white',
                  border: active ? `2px solid ${T.gold}` : '1.5px solid rgba(0,0,0,0.06)',
                  boxShadow: active ? '0 8px 30px rgba(22,33,62,0.18)' : '0 4px 16px rgba(0,0,0,0.05)',
                }}>
                <span className="text-xl sm:text-2xl">{SECTION_ICONS[sec.id]}</span>
                <span className="text-xs sm:text-sm font-bold" style={{ fontFamily: 'Poppins, sans-serif', color: active ? 'white' : T.navy }}>
                  {sec.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Sub-tab Pills */}
        <div className="vrv-subtabs mb-6">
          {subTabs.map(sub => {
            const active = effectiveSubTab === sub.id;
            return (
              <button key={sub.id} onClick={() => setActiveSubTab(sub.id)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-wide transition-all duration-200"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  background: active ? `linear-gradient(135deg, ${T.gold}, ${T.goldDark})` : 'white',
                  color: active ? T.navyDark : T.muted,
                  border: active ? 'none' : '1.5px solid rgba(0,0,0,0.08)',
                  boxShadow: active ? '0 4px 14px rgba(212,175,55,0.3)' : 'none',
                  minHeight: '40px',
                  flexShrink: 0,
                }}>
                <span>{SUBTAB_ICONS[sub.id]}</span>
                <span className="hidden sm:inline">{sub.label}</span>
                <span className="sm:hidden">{sub.id === 'create' ? 'Create' : sub.id === 'approve' ? 'Approve' : sub.id === 'my' ? 'Mine' : 'Manage'}</span>
              </button>
            );
          })}
        </div>

        {/* Content Panel */}
        <div>
          {activeSection === 'videos'   && effectiveSubTab === 'create'  && canCreate       && <CreateVideoSection />}
          {activeSection === 'videos'   && effectiveSubTab === 'approve' && canApprove       && <ApproveVideosSection />}
          {activeSection === 'videos'   && effectiveSubTab === 'my'      && canSeeMyContent  && <MyVideosSection />}
          {activeSection === 'videos'   && effectiveSubTab === 'manage'  && isAdmin          && <AdminYoutubeTab />}
          {activeSection === 'articles' && effectiveSubTab === 'create'  && canCreate        && <CreateArticleSection />}
          {activeSection === 'articles' && effectiveSubTab === 'approve' && canApprove       && <ApproveArticlesSection />}
          {activeSection === 'articles' && effectiveSubTab === 'my'      && canSeeMyContent  && <MyArticlesSection />}
          {activeSection === 'articles' && effectiveSubTab === 'manage'  && isAdmin          && <AdminArticlesTab />}
          {activeSection === 'events'   && effectiveSubTab === 'create'  && isAdmin          && <CreateEventSection />}
          {activeSection === 'events'   && effectiveSubTab === 'my'      && isAdmin          && <MyEventsSection />}
          {activeSection === 'events'   && effectiveSubTab === 'manage'  && isAdmin          && <AdminEventsTab />}
          {activeSection === 'events'   && effectiveSubTab === 'approve' && canApprove       && <ApproveEventsSection />}
        </div>
      </div>
    </div>
  );
};

export default ContentManagementPage;
